import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { servicesAPI } from '../api';
import { Lightbulb, Volume2, Home, Layers, Film, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SectionHeading from './SectionHeading';

const EASE = [0.16, 1, 0.3, 1];

const iconMap = {
  '1': Layers,
  '2': Lightbulb,
  '3': Volume2,
  '4': Home,
  '5': Film,
};

const ICON_FALLBACK = [Layers, Lightbulb, Volume2, Home, Film];

const ServicesSection = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();

  const serviceTranslations = {
    '1': { es: 'Proyectos Integrales', en: 'Integral Projects' },
    '2': { es: 'Iluminación Inteligente', en: 'Smart Lighting' },
    '3': { es: 'Sistemas Audiovisuales', en: 'Audiovisual Systems' },
    '4': { es: 'Automatización y Control', en: 'Automation & Control' },
    '5': { es: 'Cines Privados', en: 'Private Cinemas' },
  };

  const descTranslations = {
    '1': { es: 'Desarrollamos soluciones completas que abarcan todos los aspectos de la automatización moderna.', en: 'We develop complete solutions covering all aspects of modern automation.' },
    '2': { es: 'Diseñamos sistemas de iluminación que transforman ambientes y optimizan el consumo energético.', en: 'We design lighting systems that transform environments and optimize energy consumption.' },
    '3': { es: 'Implementamos tecnología de entretenimiento de vanguardia que ofrece experiencias inmersivas.', en: 'We implement cutting-edge entertainment technology that delivers immersive experiences.' },
    '4': { es: 'Instalamos sistemas de domótica que centralizan el control de todos los elementos de su espacio.', en: 'We install home automation systems that centralize control of all elements in your space.' },
    '5': { es: 'Creamos experiencias cinematográficas exclusivas en la comodidad de su hogar.', en: 'We create exclusive cinematic experiences in the comfort of your home.' },
  };

  useEffect(() => {
    const loadServices = async () => {
      try {
        const data = await servicesAPI.getAll();
        setServices(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading services:', error);
        setLoading(false);
      }
    };
    loadServices();
  }, []);

  if (loading) {
    return (
      <section id="servicios" className="bg-obsidian py-28">
        <div className="container mx-auto px-6 text-center">
          <p className="text-white/40">{language === 'es' ? 'Cargando servicios...' : 'Loading services...'}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="servicios" className="bg-obsidian py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[36rem] h-[36rem] bg-brand/8 rounded-full blur-[140px] pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-10 relative z-10">
        <SectionHeading
          overline={t('services.sectionTitle')}
          title={t('services.title1')}
          accent={t('services.title2')}
          subtitle={t('services.subtitle')}
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-5">
          {services.map((service, index) => {
            const Icon = iconMap[service.id] || ICON_FALLBACK[index % ICON_FALLBACK.length];
            const title = serviceTranslations[service.id]?.[language] || service.title;
            const desc = descTranslations[service.id]?.[language] || service.description;
            const wide = index === 0;
            return (
              <motion.div
                key={service.id}
                className={wide ? 'lg:col-span-3 md:col-span-2' : 'lg:col-span-3'}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, ease: EASE, delay: index * 0.08 }}
              >
                <Link
                  to="/servicios"
                  data-testid={`service-card-${service.id}`}
                  className="glass-card hover-lift rounded-2xl p-8 md:p-10 h-full flex flex-col cursor-pointer group block"
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-14 h-14 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand-light group-hover:bg-brand group-hover:text-white transition-all duration-400">
                      <Icon size={26} strokeWidth={1.5} />
                    </div>
                    <ArrowUpRight size={22} className="text-white/20 group-hover:text-brand-light group-hover:rotate-12 transition-all duration-400" />
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-medium text-white mb-3 tracking-tight">{title}</h3>
                  <p className="text-white/50 text-sm md:text-base leading-relaxed">
                    {desc}
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
